/**
 * Shared confirmation dialog wrapper for website forms.
 */

import { type CSSProperties, type ReactNode } from "react";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";

type DialogAction = {
  label: string;
  onClick: () => void;
  variant?: "default" | "outline" | "ghost";
};

interface WebsiteFormConfirmationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: ReactNode;
  icon?: ReactNode;
  iconContainerStyle?: CSSProperties;
  primaryAction: DialogAction;
  secondaryAction?: DialogAction;
}

export function WebsiteFormConfirmationDialog({
  open,
  onOpenChange,
  title,
  description,
  icon,
  iconContainerStyle,
  primaryAction,
  secondaryAction,
}: WebsiteFormConfirmationDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <div className="flex items-center gap-3 mb-2">
            {icon && (
              <div className="p-3 rounded-full" style={iconContainerStyle}>
                {icon}
              </div>
            )}
            <DialogTitle>{title}</DialogTitle>
          </div>
          <DialogDescription asChild>{description}</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button onClick={primaryAction.onClick}>
            {primaryAction.label}
          </Button>
          {secondaryAction && (
            <Button
              variant={secondaryAction.variant ?? "outline"}
              onClick={secondaryAction.onClick}
            >
              {secondaryAction.label}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
